import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import * as styles from '../styles/components/project-view.css.ts';
import logger from '../utils/logger';
import { useCommandExecutor } from '../hooks/useCommandExecutor';

export interface TabOption {
  id: string;
  value?: string;
  label: string;
  icon: string;
  description?: string;
  action: (event?: React.MouseEvent | React.KeyboardEvent) => void;
}

interface TabCreationComboBoxProps {
  options: TabOption[];
  placeholder?: string;
  projectName?: string;
  value?: string;
  onChange?: (value: string) => void;
}

export interface TabCreationComboBoxRef {
  focus: () => void;
  open: () => void;
  getTabOptions: () => TabOption[];
  handleSelect: (option: TabOption) => void;
}

/**
 * TabCreationComboBox - タブ作成用のコンボボックスコンポーネント
 * MS OfficeやVS Codeのようなドロップダウンリストを提供します
 */
const TabCreationComboBox = forwardRef<TabCreationComboBoxRef, TabCreationComboBoxProps>(({
  options,
  placeholder = "Press ↓ or type to call functions...",
  projectName,
  value = "",
  onChange
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isExecutingCommand, setIsExecutingCommand] = useState(false);

  const comboBoxRef = useRef<HTMLDivElement>(null);

  // コマンド実行フック
  const { isCommandMode, execute: executeCommand } = useCommandExecutor();

  // コマンドモードかどうか
  const inCommandMode = useMemo(() => isCommandMode(inputValue), [inputValue, isCommandMode]);

  // 外部からinputにフォーカスできるように公開
  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
      setIsOpen(true);
    },
    open: () => {
      inputRef.current?.focus();
      setIsOpen(true);
    },
    getTabOptions: () => options,
    handleSelect: (option: TabOption) => handleOptionSelect(option)
  }));
  const inputRef = useRef<HTMLInputElement>(null);

  // 外部クリックでドロップダウンを閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (comboBoxRef.current && !comboBoxRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // フィルタリング処理をメモ化
  const filteredOptions = useMemo(() => {
    if (inputValue === "") {
      return options;
    }
    const lowerValue = inputValue.toLowerCase();
    return options.filter((option, index) => {
      // optionやlabelがundefinedの場合は異常事態なのでassertで落とす
      logger.assert(option != null, `Option at index ${index} is null or undefined`, { 
        options: options.map((o, i) => ({ index: i, id: o?.id, label: o?.label, hasLabel: o?.label != null })),
        index,
        totalOptions: options.length 
      });
      logger.assert(option.label != null, `Option label at index ${index} is null or undefined`, { 
        option,
        optionKeys: option ? Object.keys(option) : null,
        allOptions: options.map((o, i) => ({ index: i, id: o?.id, label: o?.label, hasLabel: o?.label != null })),
        index,
        totalOptions: options.length 
      });
      
      return option.label.toLowerCase().includes(lowerValue) ||
        (option.description && option.description.toLowerCase().includes(lowerValue));
    });
  }, [inputValue, options]);

  // フィルタリング結果が変わったらハイライトをリセット
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filteredOptions]);

  // 入力変更ハンドラー
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    onChange?.(newValue);
  };

  // 入力フォーカスハンドラー
  const handleInputFocus = () => {
    setIsOpen(true);
    setIsFocused(true);
  };

  // 入力ブラーハンドラー
  const handleInputBlur = () => {
    setIsFocused(false);
  };

  // オプション選択ハンドラー
  const handleOptionSelect = (option: TabOption, event?: React.MouseEvent | React.KeyboardEvent) => {

    setInputValue(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange?.(option.label);

    // タブ作成アクションを実行（イベントを渡す）
    option.action(event);

    // 少し待ってから入力値をクリア（ユーザーが選択を確認できるように）
    setTimeout(() => {
      setInputValue("");
      onChange?.("");
    }, 500);
  };

  // コマンド実行ハンドラー
  const handleCommandExecution = async () => {
    if (!inputValue || isExecutingCommand) return;

    setIsExecutingCommand(true);
    setIsOpen(false);

    try {
      const result = await executeCommand(inputValue);

      // 結果はコンソールに出力されている（AIエージェント向け）
      // UIではフィードバックのために短いメッセージを表示
      if (result.success) {
        logger.info('[TabCreationComboBox] Command executed successfully', {
          command: inputValue,
          result
        });
      } else {
        logger.warn('[TabCreationComboBox] Command execution failed', {
          command: inputValue,
          error: result.error
        });
      }
    } catch (error) {
      logger.error('[TabCreationComboBox] Command execution error', {
        command: inputValue,
        error
      });
    } finally {
      setIsExecutingCommand(false);
      // 入力をクリア
      setInputValue("");
      onChange?.("");
    }
  };

  // キーボードナビゲーション
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'Enter') {
        setIsOpen(true);
        return;
      }
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
        break;
        
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
        break;
        
      case 'Enter':
        event.preventDefault();
        // コマンドモードの場合はコマンドを実行
        if (inCommandMode) {
          handleCommandExecution();
        } else if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleOptionSelect(filteredOptions[highlightedIndex], event);
        } else if (filteredOptions.length === 1) {
          // 1つのオプションのみが残っている場合は自動選択
          handleOptionSelect(filteredOptions[0], event);
        }
        break;
        
      case 'Escape':
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
        
      case 'Tab':
        setIsOpen(false);
        setIsFocused(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // ドロップダウン切り替えハンドラー
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      inputRef.current?.focus();
    }
  };

  // プレースホルダーテキストを動的に決定
  const getPlaceholderText = () => {
    if (isFocused || inputValue) {
      return "Type to search...";
    }
    return projectName || placeholder;
  };

  return (
    <div className={styles.tabCreationComboBox} ref={comboBoxRef}>
      <input
        ref={inputRef}
        type="text"
        className={styles.comboBoxInput}
        value={inputValue}
        placeholder={getPlaceholderText()}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        onClick={toggleDropdown}
        autoComplete="off"
        data-testid="tab-creation-combobox-input"
      />
      <span 
        className={`${styles.comboBoxArrow} ${isOpen ? styles.open : ''}`}
        onClick={toggleDropdown}
      >
        ▼
      </span>
      
      {isOpen && (
        <div className={styles.comboBoxDropdown} data-testid="tab-creation-combobox-dropdown">
          {inCommandMode ? (
            // コマンドモード: ヒントを表示
            <div className={styles.comboBoxOption} style={{ color: '#666', cursor: 'default' }}>
              <span className={styles.comboBoxOptionIcon}>⌨️</span>
              <span className={styles.comboBoxOptionText}>
                {isExecutingCommand ? 'Executing command...' : 'Press Enter to execute command'}
              </span>
              <span className={styles.comboBoxOptionSubtext}>
                Type x:help for available commands
              </span>
            </div>
          ) : filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.id}
                className={`${styles.comboBoxOption} ${index === highlightedIndex ? styles.selected : ''}`}
                onClick={(e) => handleOptionSelect(option, e)}
                onMouseEnter={() => setHighlightedIndex(index)}
                data-testid={`tab-creation-option-${option.id}`}
              >
                <span className={styles.comboBoxOptionIcon}>{option.icon}</span>
                <span className={styles.comboBoxOptionText}>{option.label}</span>
                {option.description && (
                  <span className={styles.comboBoxOptionSubtext}>{option.description}</span>
                )}
              </div>
            ))
          ) : (
            <div className={styles.comboBoxOption} style={{ color: '#999', cursor: 'default' }}>
              No matching options found
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default TabCreationComboBox;
export { TabCreationComboBox };
